import api from './api';
import type { Image } from '../types';

export interface ImageListResponse {
  images: Image[];
  realGeneration: boolean;
  provider: string;   // "placeholder" | "nanobana" | "openrouter"
  imageModel: string; // e.g. "openai/dall-e-3" — set when provider === "openrouter"
}

export const imageService = {
  getImages: async (lessonId: string, version: number): Promise<ImageListResponse> => {
    const response = await api.get<{
      images: Image[];
      real_generation: boolean;
      provider: string;
      image_model: string;
    }>(`/lessons/${lessonId}/versions/${version}/images`);
    return {
      images: response.data.images,
      realGeneration: response.data.real_generation,
      provider: response.data.provider ?? 'placeholder',
      imageModel: response.data.image_model ?? '',
    };
  },

  clearImages: async (lessonId: string, version: number): Promise<void> => {
    await api.delete(`/lessons/${lessonId}/versions/${version}/images`);
  },

  seedImages: async (
    lessonId: string,
    version: number,
    placeholders: Array<{ placeholder_id: string; prompt: string; slide_number: number }>
  ): Promise<{ seeded: number; total: number }> => {
    const response = await api.post<{ seeded: number; total: number }>(
      `/lessons/${lessonId}/versions/${version}/images/seed`,
      { placeholders }
    );
    return response.data;
  },

  generateImage: async (
    lessonId: string,
    version: number,
    placeholderId: string,
    prompt?: string
  ): Promise<Image> => {
    const response = await api.post<{ image: Image }>(
      `/lessons/${lessonId}/versions/${version}/images/generate`,
      { placeholder_id: placeholderId, prompt: prompt ?? '' }
    );
    return response.data.image;
  },

  generateAllImages: async (
    lessonId: string,
    version: number
  ): Promise<{ generated: number; failed: number }> => {
    const response = await api.post<{ generated: number; failed: number }>(
      `/lessons/${lessonId}/versions/${version}/images/generate-all`
    );
    return response.data;
  },

  uploadImage: async (
    lessonId: string,
    version: number,
    placeholderId: string,
    file: File
  ): Promise<Image> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('placeholder_id', placeholderId);
    const response = await api.post<{ image: Image }>(
      `/lessons/${lessonId}/versions/${version}/images/upload`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data.image;
  },
};
